<template>
  <div class="sensor-latest-values">
    <div class="header">
      <h3 class="title" v-t="'appSensorLatestValues.latestValues'" />
      <p class="time">
        {{
          $t('appSensorLatestValues.currentTime', {
            datetime: formattedCurrentTime,
          })
        }}
      </p>
    </div>
    <div
      v-for="group in groupedValues"
      v-bind:key="group.sensorGroupId"
      class="group-container"
    >
      <h4 v-if="group.sensorGroupId" class="sensor-group-name">
        {{ group.sensorGroupName }}
      </h4>
      <div
        v-for="sensor in group.values"
        v-bind:key="sensor.sensorId"
        class="values-container"
      >
        <h5 class="sensor-name">
          {{ sensor.sensorName }}
        </h5>
        <div class="card-container">
          <card-sensor-value
            v-for="item in sensor.items"
            v-bind:key="item.sensorItemId"
            v-bind="item"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import CardSensorValue from '@/components/sensor/AppCardSensorValue.vue'
import { extractSensorGroupSensorRelation } from './SensorHelpers'
import { RestApiClient } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { toServerTz, toLocalTz } from '@/js/TimezoneHelper'
import {
  DATE_FORMAT_SLASH,
  TIME_FORMAT_SECOND,
  TIME_FORMAT_MILLISECOND,
} from '@/constants.js'

// 秒日時フォーマット
const DATETIME_FORMAT_SECOND = `${DATE_FORMAT_SLASH} ${TIME_FORMAT_SECOND}`
// ミリ秒日時フォーマット
const DATETIME_FORMAT_MILLISECOND = `${DATE_FORMAT_SLASH} ${TIME_FORMAT_MILLISECOND}`

export default {
  name: 'AppSensorLatestValues',
  components: {
    'card-sensor-value': CardSensorValue,
  },
  props: {
    /**
     * 表示可能なセンサーID
     */
    sensorIds: {
      type: Array,
      default: null,
    },
    /**
     * 表示可能なセンサーグループID
     */
    sensorGroupIds: {
      type: Array,
      default: null,
    },
    /**
     * プレイスID
     */
    placeId: {
      type: Number,
      default: null,
    },
    /**
     * ユーザーID
     */
    userId: {
      type: Number,
      default: null,
    },
    /**
     * 現在時刻
     */
    currentTime: {
      type: Object,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // 最新センサー値
      values: [],
    }
  },
  computed: {
    ...mapGetters('sensorGroup', ['sensorGroupMap']),
    /**
     * 現在時刻(フォーマット済み)
     * @type {String}
     */
    formattedCurrentTime: function () {
      if (!this.currentTime) return ''
      return this.currentTime.format(DATETIME_FORMAT_SECOND)
    },
    /**
     * APIリクエストパラメータ用現在時刻
     * @type {String}
     */
    currentTimeForApi: function () {
      if (!this.currentTime) return ''
      return toServerTz(+this.currentTime)
    },
    /**
     * 利用可能なセンサーデータ
     * @type {Array.<Object>}
     */
    sensors: function () {
      const sensorIds = this.sensorIds || this.values.map((v) => v.sensor_id)
      return this.$store.getters['sensor/sensors'].filter((s) =>
        sensorIds.includes(s.id)
      )
    },
    /**
     * センサーグループとセンサーの関連({ sensorGroupId, sensorIds })
     * @type {Array.<Object>}
     */
    sensorGroupSensorRelation: function () {
      return extractSensorGroupSensorRelation(this.sensors)
    },
    /**
     * カード用最新センサー値
     * @type {Array.<Object>}
     */
    valuesForDisplay: function () {
      const result = []
      let lastSensorId = null
      for (const value of this.values) {
        const item = this.$_toValueForCard(value)
        const { sensor_id, sensor_name } = value
        if (lastSensorId === sensor_id) {
          result[result.length - 1].items.push(item)
        } else {
          result.push({
            sensorId: sensor_id,
            sensorName: sensor_name,
            items: [item],
          })
        }
        lastSensorId = sensor_id
      }
      return result
    },
    /**
     * 利用可能なセンサーグループID
     * @type {number[]}
     */
    availableSensorGroupIds: function () {
      return this.sensorGroupSensorRelation.reduce((ids, { sensorGroupId }) => {
        sensorGroupId && ids.push(sensorGroupId)
        return ids
      }, [])
    },
    /**
     * センサーグループでまとめた表示用センサー値データ
     * @type {Array.<Object>}
     */
    groupedValues: function () {
      const result = []

      // グループ選択分
      for (const gid of this.sensorGroupIds || this.availableSensorGroupIds) {
        const relation = this.sensorGroupSensorRelation.find(
          (r) => r.sensorGroupId === gid
        )
        if (relation) {
          const sensorGroupName =
            this.sensorGroupMap.get(gid)?.sensor_group_name
          result.push({
            sensorGroupId: gid,
            sensorGroupName,
            values: relation.sensorIds.map((id) =>
              this.valuesForDisplay.find((v) => v.sensorId === id)
            ),
          })
        }
      }
      result.sort((a, b) => a.sensorGroupId - b.sensorGroupId)

      // 個別センサー
      const alreadyDisplayedSensorIdSet = new Set(
        result.reduce((ids, current) => {
          ids.push(...current.values.map((v) => v.sensorId))
          return ids
        }, [])
      )
      result.push({
        sensorGroupId: null,
        sensorGroupName: '',
        values: this.valuesForDisplay.filter((v) => {
          if (alreadyDisplayedSensorIdSet.has(v.sensorId)) return false
          if (!this.sensorIds) return true
          return this.sensorIds.includes(v.sensorId)
        }),
      })

      return result
    },
  },
  watch: {
    currentTime: function () {
      if (this.placeId || this.userId) this.$_getLatestSensorValues()
    },
    placeId: function (value) {
      value && this.$_getLatestSensorValues()
    },
    userId: function (value) {
      value && this.$_getLatestSensorValues()
    },
  },
  methods: {
    /**
     * 最新センサー値取得
     */
    $_getLatestSensorValues: async function () {
      const url = RestApiClient.createApiUrl('sensor_data/at')
      const param = url.searchParams
      this.placeId && param.append('place_id', this.placeId)
      this.userId && param.append('user_id', this.userId)
      this.currentTimeForApi &&
        param.append('sensor_at', this.currentTimeForApi)

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.values = json.data.map((d) => ({
          ...d,
          sensor_at: toLocalTz(d.sensor_at, DATETIME_FORMAT_MILLISECOND),
        }))
      } catch (error) {
        this.handleApiError(error)
      }
    },

    /**
     * カード表示用valueに変換
     * @param {Object} value センサー計測値データ
     * @returns {Object} カード表示用value
     */
    $_toValueForCard: function (value) {
      const {
        sensor_at,
        sensor_item_id,
        sensor_item_name,
        value_type,
        value: val,
        unit,
      } = value
      const momentTime = moment(sensor_at)
      return {
        sensorItemId: sensor_item_id,
        time: momentTime.isValid()
          ? momentTime.format(DATETIME_FORMAT_MILLISECOND)
          : '-',
        name: sensor_item_name,
        value: value_type ? this.$_getTypeValueText(value) : val,
        unit,
      }
    },

    /**
     * コード定義済みの値のテキストを取得
     * @param {Object} value センサー計測値データ
     * @returns {String} コード定義された値のテキスト
     */
    $_getTypeValueText: function (value) {
      const { value_type, value: val } = value
      if (Array.isArray(value_type)) {
        const target = value_type.find((vt) => vt.value == val)
        if (target) return target.text
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.sensor-latest-values {
  width: 100%;
  height: 100%;
}

.header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .title {
    font-size: $font-size-middle;
    color: $text-color;
    margin: 0;
  }

  .time {
    font-size: $font-size-small;
    color: #959595;
    margin: 0;
  }
}

.group-container {
  width: 100%;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 8px;
  border-bottom: 1px solid #cfd5db;

  .sensor-group-name {
    font-size: $font-size-small;
    font-weight: $font-weight-bold;
    margin: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;

    .values-container {
      margin-bottom: 0;
    }
  }
}

.values-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  .sensor-name {
    font-size: $font-size-small;
    font-weight: $font-weight-normal;
    margin: 0;
    line-height: 1;
  }
}

.card-container {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(50% - 2px), 1fr));
  row-gap: 4px;
  column-gap: 4px;
}
</style>
